/* eslint-disable no-shadow */
/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Box, Typography, Container, Stack } from '@mui/material';

const AITerms = () => (
  <Box
    component="main"
    sx={{ flexGrow: 1, position: 'relative', zIndex: 10, pt: { xs: 4, md: 6 }, pb: 6 }}
  >
    <Container maxWidth="md" sx={{ px: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box mb={4} textAlign="left">
        <Typography
          variant="overline"
          color="primary.main"
          fontWeight={800}
          letterSpacing={1.5}
          display="block"
          mb={1}
        >
          LEGAL
        </Typography>
        <Typography
          variant="h3"
          component="h1"
          color="text.primary"
          mb={1.5}
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.5px',
            fontWeight: 700,
          }}
        >
          AI Features Terms & Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: 18-Apr-2026
        </Typography>
      </Box>

      {/* Content Section */}
      <Box>
        {/* Preamble Box */}
        <Box
          mb={4}
          p={3}
          sx={{ bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Important - please read carefully. By accessing or using any AI-powered features on
            MeruDesk.com, you acknowledge that you have read, understood, and agree to be legally
            bound by these AI Features Terms ("Terms"), in addition to our general Terms of Service
            and Privacy Policy. If you do not agree, discontinue use immediately.
          </Typography>
        </Box>

        <Stack spacing={3}>
          {/* Section 1 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              1. Definitions
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              For the purposes of these Terms, the following definitions apply:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>"Platform"</strong> refers to MeruDesk.com and all associated subdomains,
                APIs, and applications operated by CyberMeru Technologies.
              </li>
              <li>
                <strong>"AI Features"</strong> means any functionality powered by machine learning,
                large language models, generative AI, or automated decision-making systems made
                available through the Platform.
              </li>
              <li>
                <strong>"AI Output"</strong> means any text, image, code, analysis, recommendation,
                or other content generated by AI Features in response to User Input.
              </li>
              <li>
                <strong>"User Input"</strong> means any data, text, files, or instructions submitted
                by the User to AI Features.
              </li>
              <li>
                <strong>"Company"</strong> refers to CyberMeru Technologies Private Limited, its
                subsidiaries, affiliates, officers, directors, employees, and agents.
              </li>
              <li>
                <strong>"User" / "You"</strong> refers to any individual or entity accessing or
                using the AI Features.
              </li>
              <li>
                <strong>"Third-Party AI Providers"</strong> refers to any upstream AI model or API
                providers (including but not limited to Anthropic, OpenAI, Google, or similar) whose
                services underpin certain AI Features.
              </li>
            </Box>
          </Box>

          {/* Section 2 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              2. Nature of AI outputs - no warranty of accuracy
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              AI Features are experimental and probabilistic in nature. The Company makes no
              representation or warranty - express, implied, or statutory - that AI Outputs will be
              accurate, complete, current, reliable, error-free, legally compliant, or fit for any
              particular purpose.
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                mb: 2,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                AI Outputs may contain factual errors, omissions, outdated information, or content
                that is inappropriate for your specific use case.
              </li>
              <li>
                AI Outputs do not constitute professional advice of any kind, including legal,
                financial, medical, psychological, engineering, or regulatory advice.
              </li>
              <li>
                You are solely responsible for independently verifying any AI Output before relying
                on it for any decision, action, or communication.
              </li>
              <li>
                Nothing in any AI Output creates an attorney-client, fiduciary, advisory, or any
                other professional relationship between you and the Company.
              </li>
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>
              Critical disclaimer: Do not use AI Outputs as a substitute for qualified professional
              consultation, especially in high-stakes situations involving health, safety, legal
              obligations, or financial commitments.
            </Typography>
          </Box>

          {/* Section 3 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              3. Permitted and prohibited uses
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Access to AI Features is granted for lawful, personal or business purposes only. You
              agree not to use AI Features to:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                mb: 2,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                Generate, distribute, or facilitate content that is defamatory, obscene, hateful,
                harassing, threatening, or otherwise unlawful under applicable law.
              </li>
              <li>
                Produce misinformation, deepfakes, fraudulent content, or impersonate any person or
                entity.
              </li>
              <li>
                Circumvent, disable, or interfere with safety filters, rate limits, or access
                controls embedded in the Platform.
              </li>
              <li>
                Extract, scrape, reverse engineer, or attempt to replicate underlying AI models,
                weights, or training data.
              </li>
              <li>
                Violate any applicable export control laws, sanctions, or regulatory requirements.
              </li>
              <li>
                Process or submit sensitive personal data (as defined under applicable data
                protection law) unless you have obtained all required consents and complied with
                relevant legal obligations.
              </li>
              <li>
                Engage in automated bulk usage, API abuse, or any activity that places unreasonable
                load on the Platform's infrastructure without prior written approval.
              </li>
              <li>
                Use AI Outputs for surveillance, targeting of individuals, or any activity that
                infringes on others' fundamental rights.
              </li>
            </Box>
            <Typography variant="body1" color="text.secondary">
              The Company reserves the right to suspend or terminate access to AI Features at any
              time, with or without notice, for any violation of these Terms.
            </Typography>
          </Box>

          {/* Section 4 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              4. Intellectual property - ownership of inputs and outputs
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>User Input:</strong> You retain all rights you hold in your User Input. By
                submitting User Input, you grant the Company a non-exclusive, worldwide,
                royalty-free licence to process it solely to provide and improve AI Features,
                subject to our Privacy Policy.
              </li>
              <li>
                <strong>AI Output ownership:</strong> To the extent AI Outputs are capable of
                copyright protection, the Company assigns any such rights to you, subject to your
                compliance with these Terms. You are solely responsible for ensuring AI Outputs do
                not infringe third-party intellectual property rights before any commercial use.
              </li>
              <li>
                <strong>No guarantee of uniqueness:</strong> AI Outputs are generated
                probabilistically. Substantially similar outputs may be produced for other users.
                The Company makes no guarantee of exclusivity.
              </li>
              <li>
                <strong>Company IP:</strong> All rights in the Platform, AI models (to the extent
                proprietary), software, branding, and documentation remain exclusively with the
                Company or its licensors. These Terms do not grant you any licence to the Company's
                underlying technology.
              </li>
              <li>
                <strong>Feedback:</strong> Any feedback, suggestions, or ideas you provide regarding
                AI Features may be used by the Company without restriction or compensation to you.
              </li>
            </Box>
          </Box>

          {/* Section 5 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              5. Data, privacy, and AI training
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                User Inputs and AI Outputs may be processed and stored in accordance with our
                Privacy Policy. By using AI Features, you consent to such processing.
              </li>
              <li>
                The Company may use anonymised or aggregated interaction data to improve AI models
                and Platform performance, unless you have opted out in your account settings.
              </li>
              <li>
                You must not submit any personally identifiable information of third parties,
                confidential business information, or regulated data (e.g., health records, payment
                card data) to AI Features unless expressly authorised to do so.
              </li>
              <li>
                The Company complies with applicable data protection laws, including the Information
                Technology Act, 2000, the Digital Personal Data Protection Act, 2023 (India), and,
                where applicable, the GDPR.
              </li>
              <li>
                Conversations with AI Features may be reviewed by authorised Company personnel for
                safety, quality, and compliance purposes.
              </li>
            </Box>
          </Box>

          {/* Section 6 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              6. Third-party AI providers and service dependencies
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Certain AI Features are powered by Third-Party AI Providers. You acknowledge and agree
              that:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                The Company's ability to provide AI Features is dependent on the continued
                availability and performance of Third-Party AI Providers, and the Company shall not
                be liable for any disruption, degradation, or discontinuation of such third-party
                services.
              </li>
              <li>
                Third-Party AI Providers may have their own usage policies, which apply to your use
                of AI Features in addition to these Terms.
              </li>
              <li>
                The Company does not endorse and is not responsible for the practices, policies, or
                AI outputs of Third-Party AI Providers beyond the Company's own Platform
                integrations.
              </li>
              <li>
                AI Features, models, or capabilities may be modified, withdrawn, or substituted at
                the Company's discretion, including as a result of changes by Third-Party AI
                Providers, without notice or liability.
              </li>
            </Box>
          </Box>

          {/* Section 7 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              7. Limitation of liability
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              To the maximum extent permitted by applicable law:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                The Company shall not be liable for any direct, indirect, incidental, special,
                consequential, punitive, or exemplary damages arising from your use of or reliance
                on AI Features or AI Outputs, even if the Company has been advised of the
                possibility of such damages.
              </li>
              <li>
                The Company's aggregate liability to you for any claim arising out of or relating to
                AI Features shall not exceed the amount paid by you to the Company in the three (3)
                months preceding the event giving rise to the claim, or INR 5,000 (whichever is
                lower), unless required otherwise by law.
              </li>
              <li>
                The Company shall not be liable for any loss or damage resulting from AI Output
                inaccuracies, AI hallucinations, model errors, system downtime, data loss, or
                unauthorised access to your User Input.
              </li>
              <li>
                You assume full responsibility and risk for any decisions made or actions taken
                based on AI Outputs.
              </li>
            </Box>
          </Box>

          {/* Section 8 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              8. Indemnification
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You agree to indemnify, defend, and hold harmless CyberMeru Technologies, its
              directors, employees, agents, licensors, and service providers from and against any
              claims, liabilities, damages, judgments, awards, losses, costs, or expenses (including
              reasonable legal fees) arising out of or relating to:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>Your violation of these Terms or any applicable law or regulation.</li>
              <li>
                Your User Input, including any allegation that it infringes third-party intellectual
                property rights or contains unlawful content.
              </li>
              <li>
                Your use or misuse of AI Outputs, including any commercial decisions, publications,
                or actions taken in reliance thereon.
              </li>
              <li>
                Your breach of any third-party rights, including privacy, confidentiality, or data
                protection obligations.
              </li>
            </Box>
          </Box>

          {/* Section 9 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              9. Availability, modifications, and termination
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                AI Features are provided on an "as-is" and "as-available" basis. The Company does
                not guarantee uninterrupted or error-free availability.
              </li>
              <li>
                The Company may modify, suspend, or discontinue AI Features (in whole or in part) at
                any time without prior notice or liability.
              </li>
              <li>
                The Company may update these Terms at any time. Continued use of AI Features after
                any update constitutes acceptance of the revised Terms. Material changes will be
                notified via email or a prominent notice on the Platform.
              </li>
              <li>
                The Company may immediately restrict or terminate your access to AI Features for any
                violation of these Terms, without prejudice to any other remedies available.
              </li>
            </Box>
          </Box>

          {/* Section 10 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              10. Governing law and dispute resolution
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                These Terms shall be governed by and construed in accordance with the laws of India,
                without regard to conflict of law principles.
              </li>
              <li>
                Any dispute, controversy, or claim arising out of or relating to these Terms or AI
                Features shall first be subject to good-faith negotiation. If unresolved within 30
                days, the dispute shall be referred to binding arbitration under the Arbitration and
                Conciliation Act, 1996 (India), with the seat of arbitration in Pune, Maharashtra.
              </li>
              <li>
                Notwithstanding the above, either party may seek interim or injunctive relief from a
                court of competent jurisdiction in Pune, Maharashtra to protect its rights pending
                arbitration.
              </li>
              <li>
                You waive any right to bring claims as a class action or in any representative
                capacity.
              </li>
            </Box>
          </Box>

          {/* Section 11 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              11. Age restriction and eligibility
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                AI Features are intended for users aged 18 years and above. By using AI Features,
                you represent and warrant that you meet this age requirement.
              </li>
              <li>
                If you are using AI Features on behalf of a business or legal entity, you represent
                that you have full authority to bind that entity to these Terms.
              </li>
              <li>
                Users located in jurisdictions where AI Features are prohibited or restricted by
                local law are not permitted to access them.
              </li>
            </Box>
          </Box>

          {/* Section 12 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              12. Ethical AI use and responsible conduct
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              CyberMeru Technologies is committed to responsible AI development. As a condition of
              access, you agree to use AI Features ethically and in good faith, including:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                Not attempting to manipulate AI Features into producing harmful, biased, or
                policy-violating outputs through adversarial prompting or jailbreaking.
              </li>
              <li>
                Reporting any AI Output that appears harmful, dangerous, or factually misleading to
                our trust and safety team at safety@cybermeru.com.
              </li>
              <li>
                Acknowledging to end-recipients, where required by law, that content has been
                generated with AI assistance.
              </li>
              <li>
                Complying with any sector-specific AI regulations applicable to your use case, such
                as those governing healthcare, financial services, or public communications.
              </li>
            </Box>
          </Box>

          {/* Section 13 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              13. Miscellaneous
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Severability:</strong> If any provision of these Terms is found
                unenforceable, the remaining provisions continue in full force and effect.
              </li>
              <li>
                <strong>Waiver:</strong> Failure to enforce any right under these Terms shall not
                constitue a waiver of that right.
              </li>
              <li>
                <strong>Entire agreement:</strong> These Terms, together with the general Terms of
                Service and Privacy Policy, constitute the entire agreement between you and the
                Company regarding AI Features.
              </li>
              <li>
                <strong>Assignment:</strong> You may not assign your rights or obligations under
                these Terms without prior written consent. The Company may freely assign these
                Terms.
              </li>
              <li>
                <strong>Force majeure:</strong> The Company shall not be liable for any failure or
                delay in providing AI Features due to causes beyond its reasonable control,
                including natural disasters, government actions, or third-party provider outages.
              </li>
              <li>
                <strong>Language:</strong> These Terms are written in English. In the event of any
                translation, the English version shall prevail.
              </li>
            </Box>
          </Box>
        </Stack>
      </Box>
    </Container>
  </Box>
);

export default AITerms;
